import { InfoOutlined } from '@mui/icons-material';
import {
  Autocomplete as MuiAutocomplete,
  type AutocompleteProps as MuiAutocompleteProps,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from '@mui/material';
import { forwardRef } from 'react';

import { Tooltip } from '../Tooltip/Tooltip';

export type AutocompleteProps<T> = Omit<MuiAutocompleteProps<T, false, false, false>, 'renderInput'> & {
  helperText?: string;
  label?: string;
  required?: boolean;
  error?: string;
  name?: string;
  startAdornment?: React.ReactNode;
  infoTooltip?: string;
};

const Field = <T,>(
  {
    id,
    label,
    fullWidth = true,
    required,
    error,
    value,
    helperText,
    size = 'small',
    name,
    placeholder = 'Type to search',
    startAdornment,
    infoTooltip,
    ...rest
  }: AutocompleteProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const hasError = Boolean(error);
  return (
    <FormControl
      required={required}
      error={hasError}
      size={size}
      fullWidth={fullWidth}
      sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
    >
      <Box
        sx={{
          width: fullWidth ? '100%' : null,
        }}
      >
        <MuiAutocomplete<T>
          id={id}
          fullWidth={fullWidth}
          value={value || null}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={rest.autoFocus}
              placeholder={placeholder}
              required={required}
              size={size}
              label={label}
              error={hasError}
              name={name}
              InputProps={{
                ...params.InputProps,
                endAdornment: rest.loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : (
                  params.InputProps.endAdornment
                ),
                startAdornment: startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>,
              }}
            />
          )}
          {...rest}
          ref={ref}
        />

        {hasError ? <FormHelperText>{error}</FormHelperText> : null}
        {helperText && !hasError ? <FormHelperText>{helperText}</FormHelperText> : null}
      </Box>
      {infoTooltip ? (
        <Tooltip title={infoTooltip}>
          <InfoOutlined sx={{ color: 'secondary.main', marginTop: 1 }} />
        </Tooltip>
      ) : null}
    </FormControl>
  );
};

export const Autocomplete = forwardRef(Field) as <T>(props: AutocompleteProps<T>) => JSX.Element;
